import api from '../../../api';
import { ResponseSuccess } from '../../../shared/types';
import {
  ApiAction,
  BulkAllEmailAccountWarmupSettingsPayload,
  BulkEmailAccountWarmupSettingsPayload,
  EmailAccountBulkActionFilters,
  EmailAccountId,
  EmailAccountTagsAssignPayload,
  EmailAccountTagsBulkAssignPayload,
  EmailAccountTagsBulkUnassignPayload,
  EmailAccountTagsUnassignPayload,
  EmailAccountWarmupFilters,
  EmailAccountWarmupSettingsPayload,
  EmailAccountsWarmupExportPayload,
  EmailWarmupDeliverabilityPayload,
  EmailWarmupErrorMessagePayload,
  EmailWarmupReportFiltersPayload,
  EmailActivitySummaryPayload,
  EmailActivityEventsPayload,
  WarmupActivityHistoryPayload,
} from '../types';

export const getEmailAccountsInWarmup = async (
  options: EmailAccountWarmupFilters,
) =>
  api.post(`email-account/email-warmup`, {
    ...options,
  });

export const getEmailAccountWarmupDeliverabilityReport = async ({
  id,
  timeFrame,
}: EmailWarmupDeliverabilityPayload) =>
  api.get(`email-account/email-warmup/deliverability/${id}`, {
    params: {
      timeFrame,
    },
  });

export const getEmailAccountWarmupList = async () =>
  api.get('email-account/email-warmup/list');

export const getEmailAccountWarmupSentReceivedReport = async ({
  id,
}: EmailAccountId) => api.get(`email-account/email-warmup/sent-received/${id}`);

export const getEmailAccountWarmupSettings = async ({ id }: EmailAccountId) =>
  api.get(`email-account/email-warmup/settings/${id}`);

export const getEmailAccountWarmupBulkRunningStoppedStatus = async (
  emailFilters: EmailAccountWarmupFilters,
  emailAccountIds: number[] = [],
) =>
  api.post(`email-account/email-warmup/bulk-count`, {
    ...emailFilters,
    emailAccountIds,
  });

export const saveBulkEmailAccountWarmupSettings = async ({
  ...rest
}: BulkEmailAccountWarmupSettingsPayload) =>
  api.patch('email-account/bulk-email-warmup/settings', rest);

export const saveEmailAccountWarmupSettings = async ({
  id,
  ...rest
}: EmailAccountWarmupSettingsPayload) =>
  api.patch(`email-account/email-warmup/settings/${id}`, rest);

export const sendTableEmailWarmupSettingsStrategyEdit = async ({
  id,
  ...rest
}: EmailAccountWarmupSettingsPayload) =>
  api.patch(`/email-account/email-warmup/list/${id}`, rest);

export const turnOnOffEmailAccountWarmup = async ({ id }: EmailAccountId) =>
  api.patch(`email-account/email-warmup/${id}`);

export const importEmailAccountCsv = (file: File) => {
  const data = new FormData();
  data.append('file', file);

  return api.post('/email-account/bulk-connect', data);
};

export const syncSaleshandyEmailAccountWithApiKey = (apiKey: string) =>
  api.post('/sh-api-key/verify', {
    apiKey,
  });

export const getSaleshandyApiKey = () => api.get('/sh-api-key');

export const getTagsInEmailWarmup = async () =>
  api.get<ResponseSuccess>(`/email-account/tags/list`);

export const getEmailAccountTags = async () =>
  api.get('/email-account/tags/list');

export const emailAccountTagsAssign = async (
  payload: EmailAccountTagsAssignPayload,
) => api.post('/email-account/tag/assign', payload);

export const emailAccountTagsBulkAssign = async (
  payload: EmailAccountTagsBulkAssignPayload,
) => api.post('/email-account/tag/bulk-assign', payload);

export const emailAccountTagsUnassign = async (
  payload: EmailAccountTagsUnassignPayload,
) => api.post('/email-account/tag/un-assign', payload);

export const emailAccountTagsBulkUnassign = async (
  payload: EmailAccountTagsBulkUnassignPayload,
) => api.post('/email-account/tag/bulk-un-assign', payload);

export const updateAllEmailAccountWarmupSettings = async (
  payload: BulkAllEmailAccountWarmupSettingsPayload,
) => api.patch('/email-account/bulk-email-warmup/bulk-settings', payload);

export const enableDisableDeleteSelectedEmailAccountWarmup = async (
  apiActon: ApiAction,
  emailAccountIds: number[],
) =>
  api.post(`/email-account/email-warmup/${apiActon}/multi-select`, {
    emailAccountIds,
  });

export const enableDisableDeleteBulkEmailAccountWarmup = async (
  apiAction: ApiAction,
  deSelectedEmailAccountIds: number[],
  emailFilters?: EmailAccountBulkActionFilters,
) =>
  api.post(`/email-account/email-warmup/${apiAction}/bulk`, {
    deSelectedEmailAccountIds,
    emailFilters,
  });

export const getEmailAccountsBulkActionStatus = async () =>
  api.get<ResponseSuccess>('/email-account/email-warmup-bulk-status');

export const refreshEmailAccount = async (emailAccountId: number) =>
  api.get(`/email-account/email-setup-score/${emailAccountId}?refresh=true`);

export const saveEmailWarmupErrorMessage = async ({
  emailAccountId,
  errorMessage,
}: EmailWarmupErrorMessagePayload) =>
  api.post<ResponseSuccess>(
    `email-account/email-warmup/error-message/${emailAccountId}`,
    { errorMessage },
  );

export const getEmailAccountWarmupReportStats = async ({
  id,
  startDate,
  endDate,
  esps,
}: EmailWarmupReportFiltersPayload) =>
  api.post<ResponseSuccess>(`/reports/email-account-warmup-stats/${id}`, {
    startDate,
    endDate,
    esps,
  });

export const getEmailAccountWarmupReportDeliverabilityScore = async ({
  id,
  startDate,
  endDate,
  esps,
}: EmailWarmupReportFiltersPayload) =>
  api.post<ResponseSuccess>(
    `/reports/email-account-warmup-deliverability-score/${id}`,
    {
      startDate,
      endDate,
      esps,
    },
  );

export const getEmailAccountWarmupReportESPBreakdown = async ({
  id,
  startDate,
  endDate,
  esps,
}: EmailWarmupReportFiltersPayload) =>
  api.post<ResponseSuccess>(
    `/reports/email-account-warmup-esp-based-breakdown/${id}`,
    {
      startDate,
      endDate,
      esps,
    },
  );

export const getEmailAccountWarmupReportGraph = async ({
  id,
  startDate,
  endDate,
  esps,
  granularity,
}: EmailWarmupReportFiltersPayload) =>
  api.post<ResponseSuccess>(
    `/reports/email-account-warmup-esp-based-analytics/${id}`,
    {
      startDate,
      endDate,
      esps,
      granularity,
    },
  );

export const getEmailAccountWarmupReportExport = async ({
  id,
  startDate,
  endDate,
  esps,
}: EmailWarmupReportFiltersPayload) =>
  api.post<ResponseSuccess>(`/reports/email-account-warmup-export/${id}`, {
    startDate,
    endDate,
    esps,
  });

/** Bulk warmup export: one call for selected accounts or select-all with filters. */
export const emailAccountsWarmupExport = async (
  payload: EmailAccountsWarmupExportPayload,
) => {
  const body: Record<string, unknown> = {
    startDate: payload.startDate,
    endDate: payload.endDate,
    esps: payload.esps,
  };
  if (payload.deSelectedEmailAccountIds !== undefined) {
    body.deSelectedEmailAccountIds = payload.deSelectedEmailAccountIds;
    body.emailFilters = payload.emailFilters ?? {};
  } else if (
    payload.emailAccountIds !== undefined &&
    payload.emailAccountIds.length > 0
  ) {
    // API should receive emailAccountIds as an array, not comma-separated string
    body.emailAccountIds = payload.emailAccountIds;
  }
  return api.post<ResponseSuccess>(
    '/reports/email-accounts-warmup-export',
    body,
  );
};

export const getEmailAccountActivitySummary = async ({
  senderEmailAccountId,
}: EmailActivitySummaryPayload) =>
  api.get<ResponseSuccess>('/activity/email-journey/summary', {
    params: { senderEmailAccountId },
  });

export const getEmailAccountActivityEvents = async ({
  senderEmailAccountId,
  dayOffset,
  limit,
  lastEventAt,
}: EmailActivityEventsPayload) =>
  api.get<ResponseSuccess>('/activity/email-journey', {
    params: { senderEmailAccountId, dayOffset, limit, lastEventAt },
  });

export const getEmailAccountWarmupActivityHistory = async ({
  emailAccountId,
}: WarmupActivityHistoryPayload) =>
  api.get<ResponseSuccess>('/activity/warmup-activity', {
    params: { emailAccountId },
  });
